import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, Info } from "lucide-react";
import { SettingsShell, useSettings, SaveBar, ToggleRow } from "./SettingsShell";

/**
 * Jobs settings — 5 sub-tabs.
 * Templates sub-tab shows the "+ Job template" placeholder button only (per user directive).
 * Custom Signatures accepts up to 3 labels; capture on Job Detail is off-list.
 */

const SUBTABS = [
  { key: "general",     label: "General" },
  { key: "costs",       label: "Costs & Materials" },
  { key: "templates",   label: "Templates" },
  { key: "time",        label: "Time tracking" },
  { key: "splits",      label: "Job splits" },
];

export default function SettingsJobs() {
  const { settings, patch, save, loading, saving, saved, err } = useSettings();
  const [activeSubTab, setActiveSubTab] = useState("general");
  if (loading || !settings) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <SettingsShell
      title="Jobs"
      subtitle="Configure how jobs are created, costed, and tracked."
      testid="settings-jobs"
      subTabs={SUBTABS}
      activeSubTab={activeSubTab}
      onSubTabChange={setActiveSubTab}
    >
      {activeSubTab === "general"   && <GeneralTab settings={settings} patch={patch} />}
      {activeSubTab === "costs"     && <CostsTab settings={settings} patch={patch} />}
      {activeSubTab === "templates" && <TemplatesTab />}
      {activeSubTab === "time"      && <TimeTab settings={settings} patch={patch} />}
      {activeSubTab === "splits"    && <SplitsTab settings={settings} patch={patch} />}
      <SaveBar onSave={save} saving={saving} saved={saved} err={err} testid="settings-jobs" />
    </SettingsShell>
  );
}

function GeneralTab({ settings, patch }) {
  const sigs = Array.isArray(settings.jobs_custom_signatures) ? settings.jobs_custom_signatures : [];
  const addSig = () => sigs.length < 3 && patch({ jobs_custom_signatures: [...sigs, { label: "", required: false }] });
  const updateSig = (i, part) => patch({ jobs_custom_signatures: sigs.map((s, idx) => idx === i ? { ...s, ...part } : s) });
  const removeSig = (i) => patch({ jobs_custom_signatures: sigs.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6">
      {/* Job reminders migration banner */}
      <div data-testid="jobs-reminders-banner" className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1">
          <p className="text-sm text-blue-900">Job reminders are now managed on the <Link to="/settings/notifications" className="underline font-medium">Notifications</Link> page.</p>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <ToggleRow
          label="Appointments"
          description="Schedule specific arrival windows on jobs."
          checked={settings.jobs_appointments_enabled !== false}
          onChange={(v) => patch({ jobs_appointments_enabled: v })}
          testid="jobs-appointments"
        />
        <ToggleRow
          label="Estimates on jobs"
          description="Attach one or more estimates to a job."
          checked={settings.jobs_estimates_enabled !== false}
          onChange={(v) => patch({ jobs_estimates_enabled: v })}
          testid="jobs-estimates-on-jobs"
        />
      </section>

      <section data-testid="jobs-signatures" className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-semibold text-slate-900">Custom Signatures</h3>
            <p className="text-xs text-slate-500 mt-0.5">Up to 3 signature slots collected on job completion (capture UI on the job page is not yet wired).</p>
          </div>
          <button
            type="button"
            onClick={addSig}
            disabled={sigs.length >= 3}
            data-testid="jobs-add-signature"
            className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1.5"
          >
            <Plus size={12} /> Add signature ({sigs.length}/3)
          </button>
        </div>
        {sigs.length === 0 ? (
          <p className="text-sm text-slate-400 italic text-center py-4">No custom signatures yet.</p>
        ) : (
          <div className="space-y-2">
            {sigs.map((s, i) => (
              <div key={i} data-testid={`jobs-signature-row-${i}`} className="grid grid-cols-[1fr_140px_40px] gap-2 items-center">
                <input
                  placeholder="Signature label (e.g. Customer, Technician)"
                  value={s.label || ""}
                  onChange={(e) => updateSig(i, { label: e.target.value })}
                  data-testid={`jobs-signature-label-${i}`}
                  className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
                />
                <label className="text-xs flex items-center gap-2">
                  <input type="checkbox" checked={!!s.required} onChange={(e) => updateSig(i, { required: e.target.checked })} data-testid={`jobs-signature-required-${i}`} />
                  Required
                </label>
                <button type="button" onClick={() => removeSig(i)} data-testid={`jobs-signature-remove-${i}`} className="p-2 text-slate-400 hover:text-red-600 border border-slate-300 rounded">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CostsTab({ settings, patch }) {
  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-1">Job costing</h3>
        <p className="text-xs text-slate-500 mb-3">Track actual and expected costs on jobs to see profit margins.</p>
        <ToggleRow label="Show job costing breakdown"       description="Display costs vs revenue on the job page."  checked={settings.jobs_show_costing !== false}            onChange={(v) => patch({ jobs_show_costing: v })}            testid="jobs-show-costing" />
        <ToggleRow label="Show expected job costs"          description="Show a target cost on new jobs."              checked={!!settings.jobs_show_expected_costs}            onChange={(v) => patch({ jobs_show_expected_costs: v })}     testid="jobs-show-expected" />
        <ToggleRow label="Material inputs outside Price book" description="Add ad-hoc materials without a price book entry." checked={settings.jobs_material_outside_pricebook !== false} onChange={(v) => patch({ jobs_material_outside_pricebook: v })} testid="jobs-material-outside-pb" />
        <ToggleRow label="Miscellaneous job inputs"         description="Add fees, discounts, and other adjustments to jobs." checked={settings.jobs_misc_inputs !== false}             onChange={(v) => patch({ jobs_misc_inputs: v })}             testid="jobs-misc-inputs" />
        <ToggleRow label="Travel and time on jobs"          description="Log travel time separately from on-site work."  checked={!!settings.jobs_travel_time}                    onChange={(v) => patch({ jobs_travel_time: v })}             testid="jobs-travel-time" />
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-1">Pay and commissions</h3>
        <p className="text-xs text-slate-500">Configure hourly pay and commission structures for your team.</p>
        <div className="mt-3">
          <button
            type="button"
            disabled
            data-testid="jobs-manage-pay"
            className="text-sm text-slate-400 border border-slate-200 rounded px-3 py-1.5 cursor-not-allowed"
            title="Coming soon"
          >
            Manage pay →
          </button>
          <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-2">Coming soon</span>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-1">Material detail tracking</h3>
        <p className="text-xs text-slate-500 mb-3">Capture where materials came from and their purchase status.</p>
        <ToggleRow label="Source"          description="Where the material was purchased."      checked={!!settings.jobs_material_track_source}   onChange={(v) => patch({ jobs_material_track_source: v })}   testid="jobs-mat-source" />
        <ToggleRow label="Purchase status" description="Purchased / Pending / Returned."         checked={!!settings.jobs_material_track_purchase} onChange={(v) => patch({ jobs_material_track_purchase: v })} testid="jobs-mat-purchase" />
        <ToggleRow label="Vendor"          description="Which supplier the material came from." checked={!!settings.jobs_material_track_vendor}   onChange={(v) => patch({ jobs_material_track_vendor: v })}   testid="jobs-mat-vendor" />
      </section>
    </div>
  );
}

function TemplatesTab() {
  return (
    <section data-testid="jobs-templates" className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-semibold text-slate-900">Job templates</h3>
          <p className="text-xs text-slate-500 mt-0.5">Reusable templates for common job types (line items, checklists, tags).</p>
        </div>
        <button
          type="button"
          disabled
          data-testid="jobs-add-template"
          className="px-3 py-1.5 text-sm border border-slate-300 text-slate-400 rounded cursor-not-allowed flex items-center gap-1.5"
          title="Job templates — coming soon"
        >
          <Plus size={14} /> Job template
        </button>
      </div>
      <div className="text-center py-10 text-sm text-slate-400">
        <div className="italic mb-1">No job templates yet.</div>
        <div className="text-xs">Job templates are on the roadmap — enable them here once the feature ships.</div>
      </div>
    </section>
  );
}

function TimeTab({ settings, patch }) {
  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-3">Employee time tracking</h3>
        <ToggleRow
          label="Total clocked-in time and timesheets"
          description="Allow employees to clock in/out and view their timesheets."
          checked={true}
          onChange={() => {}}
          disabled
          testid="jobs-time-total"
        />
        <p className="text-[11px] text-slate-500 -mt-2 pl-1">Always on — Timesheets is a live page.</p>

        <ToggleRow
          label="Automatically clock in/out"
          description="Clock in when a technician marks 'On my way', clock out when the job is finished."
          checked={!!settings.jobs_auto_clock}
          onChange={(v) => patch({ jobs_auto_clock: v })}
          testid="jobs-auto-clock"
        />
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-3">Time format</h3>
        <label className="flex items-center gap-2 py-1.5 cursor-pointer">
          <input
            type="radio"
            name="time_format"
            checked={(settings.time_format || "decimal") === "decimal"}
            onChange={() => patch({ time_format: "decimal" })}
            data-testid="jobs-timeformat-decimal"
            className="text-[#0F4C81]"
          />
          <span className="text-sm text-slate-900">Decimal hours <span className="text-slate-500">(48.50)</span></span>
        </label>
        <label className="flex items-center gap-2 py-1.5 cursor-pointer">
          <input
            type="radio"
            name="time_format"
            checked={settings.time_format === "hm"}
            onChange={() => patch({ time_format: "hm" })}
            data-testid="jobs-timeformat-hm"
            className="text-[#0F4C81]"
          />
          <span className="text-sm text-slate-900">Hours & minutes <span className="text-slate-500">(48:30)</span></span>
        </label>
      </section>
    </div>
  );
}

function SplitsTab({ settings, patch }) {
  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <ToggleRow
          label="Show splits on job page"
          description="Display revenue-split percentages when multiple technicians are assigned."
          checked={!!settings.jobs_show_splits}
          onChange={(v) => patch({ jobs_show_splits: v })}
          testid="jobs-show-splits"
        />
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-1">Default job splits</h3>
        <p className="text-xs text-slate-500 mb-3">Applied automatically to new jobs with multiple assigned technicians. You can always override per-job.</p>
        <label className="flex items-start gap-2 py-2 cursor-pointer">
          <input
            type="radio"
            name="default_split"
            checked={(settings.jobs_default_split || "even") === "even"}
            onChange={() => patch({ jobs_default_split: "even" })}
            data-testid="jobs-default-split-even"
            className="mt-0.5 text-[#0F4C81]"
          />
          <span>
            <span className="text-sm text-slate-900 font-medium">Even split</span>
            <span className="block text-xs text-slate-500">2 techs: 50%/50% · 3 techs: 33%/33%/33% · etc.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 py-2 cursor-pointer">
          <input
            type="radio"
            name="default_split"
            checked={settings.jobs_default_split === "first"}
            onChange={() => patch({ jobs_default_split: "first" })}
            data-testid="jobs-default-split-first"
            className="mt-0.5 text-[#0F4C81]"
          />
          <span>
            <span className="text-sm text-slate-900 font-medium">All to first assigned</span>
            <span className="block text-xs text-slate-500">100% to the first technician assigned to the job.</span>
          </span>
        </label>
      </section>
    </div>
  );
}
